import type {
  VoiceResponse,
  VoiceSessionConfig,
  VoiceSessionEndReason,
} from '@zolotalk/shared-types';

/**
 * Voice streaming session client (REQ-VOICE-001, SPA plumbing).
 *
 * Speaks the session protocol against `/ws/voice`: in-band `auth` first on
 * every connection, then `session_start` on the first connect or
 * `session_resume` with the last delivered seq after a drop — so the server
 * replays only frames this client never saw (AC-03), and a revoked login is
 * refused on reconnect because every reconnect re-authenticates.
 */

export type VoiceSessionState =
  | 'idle'
  | 'connecting'
  | 'active'
  | 'reconnecting'
  | 'ended'
  | 'error';

export interface TranslationEvent {
  seq: number;
  transcript: string;
  translation: string;
  audioBase64: string;
  emotion?: string;
}

export interface VoiceSessionCallbacks {
  onStateChange?: (state: VoiceSessionState) => void;
  onTranslation?: (event: TranslationEvent) => void;
  onSessionEnded?: (reason: VoiceSessionEndReason) => void;
  /** Generic client-visible error codes from the channel. */
  onError?: (code: string, message: string) => void;
}

/** The browser WebSocket surface the client depends on (mockable). */
export interface VoiceWebSocket {
  readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: unknown) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export interface VoiceSessionClientOptions {
  url: string;
  config: VoiceSessionConfig;
  /** Async so callers can refresh a stale access token before connecting. */
  getAccessToken: () => string | null | Promise<string | null>;
  callbacks?: VoiceSessionCallbacks;
  /** Injectable for tests; defaults to the browser WebSocket. */
  createWebSocket?: (url: string) => VoiceWebSocket;
  maxReconnectAttempts?: number;
  reconnectBaseDelayMs?: number;
}

export interface VoiceSessionClient {
  /** Connects, authenticates, and starts the session. */
  start(): void;
  sendAudioChunk(base64Audio: string): void;
  endUtterance(): void;
  /** Graceful session end; the server confirms with `session_ended`. */
  endSession(): void;
  /** Tears the client down silently (unmount). */
  dispose(): void;
  getState(): VoiceSessionState;
  getSessionId(): string | null;
}

const WS_OPEN = 1;

/** Refusals that must not trigger an automatic reconnect. */
const FATAL_ERROR_CODES = new Set([
  'AUTH_FAILED',
  'AUTH_TIMEOUT',
  'SESSION_LIMIT_REACHED',
  'VOICE_SESSION_NOT_FOUND',
  'PROCESSING_FAILED',
]);

export function createVoiceSessionClient(options: VoiceSessionClientOptions): VoiceSessionClient {
  const callbacks = options.callbacks ?? {};
  const createWebSocket =
    options.createWebSocket ?? ((url: string) => new WebSocket(url) as unknown as VoiceWebSocket);
  const maxReconnectAttempts = options.maxReconnectAttempts ?? 5;
  const reconnectBaseDelayMs = options.reconnectBaseDelayMs ?? 500;

  let state: VoiceSessionState = 'idle';
  let socket: VoiceWebSocket | null = null;
  let sessionId: string | null = null;
  let lastSeq = 0;
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;
  let fatal = false;

  function setState(next: VoiceSessionState): void {
    if (state !== next && !disposed) {
      state = next;
      callbacks.onStateChange?.(next);
    }
  }

  function sendFrame(frame: unknown): void {
    if (socket && socket.readyState === WS_OPEN) {
      socket.send(JSON.stringify(frame));
    }
  }

  function fail(code: string, message: string): void {
    fatal = true;
    callbacks.onError?.(code, message);
    setState('error');
  }

  async function handleOpen(): Promise<void> {
    let token: string | null;
    try {
      token = await options.getAccessToken();
    } catch {
      token = null;
    }
    if (disposed) {
      return;
    }
    if (!token) {
      // No live login — the channel would refuse anyway; fail fast with a
      // code the UI can translate into a sign-in prompt.
      fail('NOT_SIGNED_IN', 'Sign in to start a voice session');
      socket?.close();
      return;
    }
    sendFrame({ type: 'auth', payload: { token } });
  }

  function handleFrame(frame: VoiceResponse): void {
    switch (frame.type) {
      case 'auth_ok':
        if (sessionId) {
          sendFrame({ type: 'session_resume', payload: { sessionId, lastSeq } });
        } else {
          sendFrame({ type: 'session_start', payload: options.config });
        }
        return;
      case 'session_started':
        sessionId = frame.payload.sessionId;
        reconnectAttempts = 0;
        setState('active');
        return;
      case 'session_resumed':
        reconnectAttempts = 0;
        setState('active');
        return;
      case 'translation':
        // Dedupe on seq: a frame replayed by an over-cautious resume is
        // dropped, never re-rendered or re-played (AC-03 client side).
        if (frame.seq > lastSeq) {
          lastSeq = frame.seq;
          callbacks.onTranslation?.({ seq: frame.seq, ...frame.payload });
        }
        return;
      case 'session_ended':
        fatal = true;
        callbacks.onSessionEnded?.(frame.payload.reason);
        setState('ended');
        return;
      case 'error':
        callbacks.onError?.(frame.payload.code, frame.payload.message);
        if (FATAL_ERROR_CODES.has(frame.payload.code)) {
          fatal = true;
          setState('error');
        }
        return;
      case 'pong':
        return;
    }
  }

  function handleClose(): void {
    socket = null;
    if (disposed || fatal || state === 'ended' || state === 'error') {
      return;
    }
    // Unexpected drop with a live session: re-authenticate and resume.
    if (sessionId && reconnectAttempts < maxReconnectAttempts) {
      reconnectAttempts += 1;
      setState('reconnecting');
      reconnectTimer = setTimeout(
        () => {
          reconnectTimer = null;
          connect();
        },
        reconnectBaseDelayMs * 2 ** (reconnectAttempts - 1)
      );
      return;
    }
    fail('CONNECTION_LOST', 'Voice session connection lost');
  }

  function connect(): void {
    if (disposed) {
      return;
    }
    if (state !== 'reconnecting') {
      setState('connecting');
    }
    const ws = createWebSocket(options.url);
    socket = ws;
    ws.onopen = () => {
      void handleOpen();
    };
    ws.onmessage = event => {
      try {
        handleFrame(JSON.parse(String(event.data)) as VoiceResponse);
      } catch {
        // Server frames are schema-bound; anything unparseable is ignored.
      }
    };
    ws.onclose = () => {
      handleClose();
    };
    ws.onerror = () => {
      // The paired close event drives state; nothing to do here.
    };
  }

  return {
    start() {
      if (state === 'idle' && !disposed) {
        connect();
      }
    },
    sendAudioChunk(base64Audio) {
      if (state === 'active') {
        sendFrame({ type: 'audio', payload: base64Audio });
      }
    },
    endUtterance() {
      if (state === 'active') {
        sendFrame({ type: 'end' });
      }
    },
    endSession() {
      if (state === 'active') {
        sendFrame({ type: 'session_end' });
      }
    },
    dispose() {
      disposed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      socket?.close();
      socket = null;
    },
    getState() {
      return state;
    },
    getSessionId() {
      return sessionId;
    },
  };
}
